
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    useTheme,
    useMediaQuery,
    Paper,
    alpha,
    Menu,
    MenuItem,
    Avatar,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    DashboardOutlined as DashboardOutlinedIcon,
    Event as EventIcon,
    EventOutlined as EventOutlinedIcon,
    PhotoLibrary as PhotoLibraryIcon,
    PhotoLibraryOutlined as PhotoLibraryOutlinedIcon,
    People as PeopleIcon,
    PeopleOutlined as PeopleOutlinedIcon,
    Contacts as ContactsIcon,
    ContactsOutlined as ContactsOutlinedIcon,
    ReportProblem as GrievanceIcon,
    ReportProblemOutlined as GrievanceOutlinedIcon,
    AccountBalance as ConstituencyIcon,
    AccountBalanceOutlined as ConstituencyOutlinedIcon,
    Person as UsersIcon,
    PersonOutlined as UsersOutlinedIcon,
    ChevronLeft as ChevronLeftIcon,
    Logout as LogoutIcon,
    AccountCircle as AccountCircleIcon,
    CalendarMonth as CalendarIcon,
    CalendarMonthOutlined as CalendarOutlinedIcon,
    Newspaper as NewsIcon,
    NewspaperOutlined as NewsOutlinedIcon,
} from '@mui/icons-material';
import logo from '../assets/OH YES.png';

const drawerWidth = 260;

const menuItems = [
    { text: 'Dashboard', icon: <DashboardOutlinedIcon />, activeIcon: <DashboardIcon />, path: '/' },
    { text: 'News', icon: <NewsOutlinedIcon />, activeIcon: <NewsIcon />, path: '/news' },
    { text: 'Calendar', icon: <CalendarOutlinedIcon />, activeIcon: <CalendarIcon />, path: '/calendar' },
    { text: 'Events', icon: <EventOutlinedIcon />, activeIcon: <EventIcon />, path: '/events' },
    { text: 'Gallery', icon: <PhotoLibraryOutlinedIcon />, activeIcon: <PhotoLibraryIcon />, path: '/gallery' },
    { text: 'Event Joinees', icon: <PeopleOutlinedIcon />, activeIcon: <PeopleIcon />, path: '/event-joinees' },
    { text: 'Directory', icon: <ContactsOutlinedIcon />, activeIcon: <ContactsIcon />, path: '/directory' },
    { text: 'Grievances', icon: <GrievanceOutlinedIcon />, activeIcon: <GrievanceIcon />, path: '/grievances' },
    { text: 'Constituency', icon: <ConstituencyOutlinedIcon />, activeIcon: <ConstituencyIcon />, path: '/constituency' },
    { text: 'Users', icon: <UsersOutlinedIcon />, activeIcon: <UsersIcon />, path: '/users' },
];

function DashboardLayout({ children }) {
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogoutClick = () => {
        setLogoutDialogOpen(true);
        handleMenuClose();
    };

    const confirmLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login', { replace: true });
        setLogoutDialogOpen(false);
        setMobileOpen(false);
    };

    const handleProfileClick = () => {
        navigate('/profile');
        handleMenuClose();
    };

    const drawer = (
        <Box sx={{ height: '100%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
            <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: [1], my: 0 }}>
                <Box sx={{
                    width: 200,
                    height: 64,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    // overflow: 'hidden',
                    // boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                    <img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Box>
                {/* <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.5, color: '#1e293b' }}>
                    Oh Yes Yes
                </Typography> */}
            </Toolbar >
            <Divider sx={{ opacity: 0.6 }} />
            <List sx={{ mt: 2, px: 2 }}>
                {menuItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
                        <ListItemButton
                            selected={location.pathname === item.path}
                            onClick={() => {
                                navigate(item.path);
                                setMobileOpen(false);
                            }}
                            sx={{
                                minHeight: 48,
                                justifyContent: 'initial',
                                px: 2,
                                borderRadius: '12px',
                                transition: 'all 0.2s',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    '& .MuiListItemIcon-root': { color: 'white' },
                                    '& .MuiListItemText-primary': { color: 'white', fontWeight: 800 },
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    }
                                },
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                    '& .MuiListItemIcon-root': { color: 'secondary.main' },
                                    '& .MuiListItemText-primary': { color: 'secondary.main', fontWeight: 600 },
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: 2.5,
                                    justifyContent: 'center',
                                    color: (location.pathname === item.path) ? 'primary.main' : '#616161ff',
                                }}
                            >
                                {location.pathname === item.path ? item.activeIcon : item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    fontSize: '0.9rem',
                                    fontWeight: (location.pathname === item.path) ? 600 : 400,
                                    color: (location.pathname === item.path) ? 'inherit' : '#616161ff'
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            <Box sx={{ mt: 'auto' }}>
                <Divider sx={{ opacity: 0.6, my: 1 }} />
                <List sx={{ px: 2, pb: 2 }}>
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            onClick={handleLogoutClick}
                            sx={{
                                minHeight: 48,
                                px: 2,
                                borderRadius: '12px',
                                color: 'error.main',
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                    '& .MuiListItemIcon-root': { color: 'error.main' },
                                    '& .MuiListItemText-primary': { fontWeight: 600 },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, mr: 2.5, justifyContent: 'center', color: 'error.main' }}>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Logout"
                                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Box>
            {/* <Box sx={{ mt: 'auto', p: 3 }}>
                <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 4 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Upgrade to Pro</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>Get more advanced features</Typography>
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', py: 0.5, px: 1, borderRadius: 1, textAlign: 'center', cursor: 'pointer' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>UPGRADE</Typography>
                    </Box>
                </Paper>
            </Box> */}
        </Box >
    );

    const getPageTitle = () => {
        const item = menuItems.find(item => item.path === location.pathname);
        return item ? item.text : '';
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(12px)',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.1),
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 1, display: { md: 'none' }, color: 'primary.main' }}
                        >
                            <MenuIcon />
                        </IconButton>
                        {/* <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{
                                fontWeight: 800,
                                color: 'primary.main',
                                fontSize: { xs: '1rem', sm: '1rem', md: '1.2rem', lg: '1.2rem', xl: '1.2rem' },
                                letterSpacing: '-0.5px'
                            }}
                        >
                            {getPageTitle()}
                        </Typography> */}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <IconButton
                            size="small"
                            onClick={handleMenuOpen}
                            sx={{
                                p: 0.5,
                                border: '2px solid',
                                borderColor: alpha(theme.palette.primary.main, 0.1),
                                transition: 'all 0.2s',
                                '&:hover': { transform: 'scale(1.05)', borderColor: 'primary.main' }
                            }}
                        >
                            <Avatar sx={{
                                width: { xs: 32, sm: 36 },
                                height: { xs: 32, sm: 36 },
                                bgcolor: 'primary.main',
                                fontSize: '1rem',
                                fontWeight: 600,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`
                            }}>
                                <AccountCircleIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    borderRadius: 3,
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                                    minWidth: 200,
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.divider, 0.1),
                                    '& .MuiMenuItem-root': {
                                        px: 2,
                                        py: 1.2,
                                        mx: 1,
                                        my: 0.5,
                                        borderRadius: 2,
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) }
                                    }
                                }
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={handleProfileClick}>
                                <ListItemIcon>
                                    <AccountCircleIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="My Profile" />
                            </MenuItem>
                            <Divider sx={{ my: '8px !important', opacity: 0.1 }} />
                            <MenuItem onClick={handleLogoutClick} sx={{ color: 'error.main' }}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                                </ListItemIcon>
                                <ListItemText primary="Log Out" />
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                {/* Mobile Drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: 'none',
                            boxShadow: '10px 0 30px rgba(0,0,0,0.05)'
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                {/* Desktop Drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.08),
                            bgcolor: '#ffffff'
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    pt: { xs: 2, sm: 3, md: 4 },
                    px: { xs: 2, sm: 3, md: 4 },
                    pb: 1, // Reduced bottom padding for even spacing
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    mt: { xs: '56px', sm: '64px' },
                    minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
                    bgcolor: '#f8fafc',
                    transition: 'all 0.3s'
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    {children}
                </Box>

                {/* Footer Section */}
                <Box
                    component="footer"
                    sx={{
                        py: 2,
                        mt: 2, // Reduced top margin
                        borderTop: '1px solid',
                        borderColor: alpha(theme.palette.divider, 0.06),
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        © {new Date().getFullYear()} Oh Yes Yes. All rights reserved.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' }
                            }}
                        >
                            Privacy Policy
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' }
                            }}
                        >
                            Terms of Service
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' }
                            }}
                        >
                            Help Center
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Logout Confirmation Dialog */}
            <Dialog
                open={logoutDialogOpen}
                onClose={() => setLogoutDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        p: 1,
                        minWidth: 320
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#1e293b' }}>
                    Confirm Logout
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Are you sure you want to log out? You will need to login again to access your dashboard.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => setLogoutDialogOpen(false)}
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmLogout}
                        variant="contained"
                        color="error"
                        autoFocus
                        sx={{
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                        }}
                    >
                        Log Out
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default DashboardLayout;
